import React, { FC, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
  colors,
  Card,
  Box
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Sale } from 'src/types';
import { formatCurrency, getPaymentTypeColor } from 'src/utils';

const useStyles = makeStyles((theme) => ({
  itemContainer: {
    marginBottom: theme.spacing(1)
  },
  serialNo: {
    color: colors.grey[600]
  },
  itemFooter: {
    display: 'flex'
  },
  quantity: {
    color: colors.purple[300],
    paddingLeft: theme.spacing(1)
  },
  dealersPrice: {
    color: colors.orange[600],
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1)
  },
  retailPrice: {
    color: colors.blue[600],
    paddingLeft: theme.spacing(1)
  }
}));

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  data: Sale;
  hideSensitive?: boolean;
}

const MuiDialogWithAccordion: FC<Props> = ({
  open,
  onClose,
  data,
  hideSensitive = false
}) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="dialog-title"
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="dialog-title">{`Transaction: ${data?.transaction_no ||
        'N/A'}`}</DialogTitle>
      <DialogContent style={{ padding: '1rem' }}>
        <Accordion style={{ marginBottom: '1rem' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="products-content"
          >
            <Typography>Products</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 5 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            >
              {data?.items?.map((product, index) => {
                return (
                  <Card
                    key={`${product?.serial_no}-${index}`}
                    style={{ padding: 10, marginBottom: 10, width: '100%' }}
                  >
                    <Typography variant="h6">
                      {product?.product_name || 'N/A'}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      className={classes.serialNo}
                    >
                      {product?.serial_no || 'N/A'}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      className={classes.quantity}
                    >
                      QTY: {product?.quantity ?? 'N/A'}
                    </Typography>
                    {!hideSensitive ? (
                      <Typography
                        variant="subtitle2"
                        className={classes.dealersPrice}
                      >
                        DP: {formatCurrency(product?.dealers_price || 0)}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="subtitle2"
                      className={classes.retailPrice}
                    >
                      RP: {formatCurrency(product?.retail_price || 0)}
                    </Typography>
                  </Card>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="products-content"
          >
            <Typography>Payments</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 5 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            >
              {data?.payments && data?.payments?.length > 0 ? (
                data?.payments?.map((payment, index) => {
                  return (
                    <Card
                      key={`${index}`}
                      style={{ padding: 10, marginBottom: 10, width: '100%' }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Typography variant="h6">
                          {`${formatCurrency(payment?.amount || 0)} `}
                        </Typography>

                        <Typography variant="h5">{` - `}</Typography>
                        <Typography
                          variant="subtitle1"
                          style={{
                            color: getPaymentTypeColor(payment?.payment_type)
                          }}
                        >
                          {` (${payment?.payment_type || 'N/A'})`}
                        </Typography>
                      </Box>

                      {payment?.acknowledgement_receipt_no ? (
                        <Typography variant="h6">
                          {`SI No: ${payment?.acknowledgement_receipt_no ||
                            'N/A'}`}
                        </Typography>
                      ) : null}

                      <Typography variant="subtitle1">
                        {`Payment Created By: ${payment?.created_by || 'N/A'}`}
                      </Typography>

                      <Typography variant="subtitle1">
                        {`Data Posted: ${payment?.date_posted || 'N/A'}`}
                      </Typography>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="h5" style={{ textAlign: 'center' }}>
                  No payment record found
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TransactionSummaryDialog = memo(MuiDialogWithAccordion);
